import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { serverDomain } from "../../config";
import "../../styles/StreamsPage.css";

const FlModal = ({ open, title, onClose, children, footer }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => e.key === "Escape" && onClose();
    const onClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) onClose();
    };

    document.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onClick);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onClick);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="flm-overlay" role="dialog" aria-label={title}>
      <div className="flm" ref={wrapRef}>
        <div className="flm__head">
          <div className="flm__title">{title}</div>
          <button className="flm__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="flm__body">{children}</div>

        {footer ? <div className="flm__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
};

const StreamsPage = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [streams, setStreams] = useState([]);
  const [domains, setDomains] = useState([]);
  const [offers, setOffers] = useState([]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [streamName, setStreamName] = useState("");
  const [domain, setDomain] = useState(1);
  const [offer, setOffer] = useState(1);
  const [landingMode, setLandingMode] = useState("none");
  const [usePostBack, setUsePostBack] = useState(false);
  const [postBackUrls, setPostBackUrls] = useState({
    registration: "",
    subscription: "",
    payment: "",
    start: "",
  });
  const [showPostbackHelp, setShowPostbackHelp] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editingStream, setEditingStream] = useState(null);
  const [editLandingMode, setEditLandingMode] = useState("none");
  const [editPostbackUrls, setEditPostbackUrls] = useState({
    registration: "",
    subscription: "",
    payment: "",
    start: "",
  });

  // ui small toasts
  const [copiedMsg, setCopiedMsg] = useState("");

  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    fetchStreams();
    fetchDomains();
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchStreams = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const response = await axios.get(`${serverDomain}/api/streams/get_streams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStreams(response.data || []);
    } catch (e) {
      console.error("Ошибка при получении потоков:", e);
    }
  };

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${serverDomain}/api/streams/domains`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains(response.data || []);
      // выставим default значения если нужно
      if ((response.data || []).length && domain === 1) {
        setDomain(response.data[0].id);
      }
    } catch (e) {
      console.error("Ошибка при получении доменов:", e);
    }
  };

  const fetchOffers = async () => {
    const token = localStorage.getItem("token");
    try {
      const [offersRes, accessRes] = await Promise.all([
        axios.get(`${serverDomain}/api/streams/offers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${serverDomain}/api/streams/offer_requests/access`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const hasAccessToOffer3 = accessRes.data?.hasAccess;
      const filtered = (offersRes.data || []).filter((o) => o.id !== 3 || hasAccessToOffer3);
      setOffers(filtered);

      if (filtered.length && offer === 1) {
        setOffer(filtered[0].id);
      }
    } catch (e) {
      console.error("Ошибка при получении офферов:", e);
    }
  };

  const domainNameById = useMemo(() => {
    const map = new Map();
    for (const d of domains) map.set(d.id, d.name || "");
    return map;
  }, [domains]);

  // 1) архив/актив
  const streamsByArchive = useMemo(() => {
    return streams.filter((s) => {
      const isArchived = Boolean(s.deleted_at);
      return showArchived ? isArchived : !isArchived;
    });
  }, [streams, showArchived]);

  // 2) поиск
  const searchedStreams = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return streamsByArchive;
    return streamsByArchive.filter((s) => {
      const ln = (s.link_name || "").toLowerCase();
      const on = (s.offer_name || "").toLowerCase();
      const dn = (domainNameById.get(s.domain) || "").toLowerCase();
      return ln.includes(q) || on.includes(q) || dn.includes(q);
    });
  }, [streamsByArchive, searchTerm, domainNameById]);

  // 3) пагинация
  const totalItems = searchedStreams.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedStreams = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return searchedStreams.slice(start, start + pageSize);
  }, [searchedStreams, currentPage]);

  useEffect(() => setCurrentPage(1), [showArchived, searchTerm]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const openCreate = () => {
    setErrorMessage("");
    setShowPostbackHelp(false);
    setLandingMode("none");
    setUsePostBack(false);
    setPostBackUrls({ registration: "", subscription: "", payment: "", start: "" });
    setStreamName("");
    setCreateOpen(true);
  };

  const validateStreamName = (value) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
    if (value !== cleaned) {
      setErrorMessage("Недопустимые символы. Используйте только английские буквы и цифры.");
    } else {
      setErrorMessage("");
    }
    return cleaned;
  };

  const handleSave = async () => {
    setErrorMessage("");
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    if (!streamName) {
      setErrorMessage("Введите название потока.");
      return;
    }
    if (!offer || !domain) {
      setErrorMessage("Выберите оффер и домен.");
      return;
    }

    try {
      await axios.post(
        `${serverDomain}/api/streams`,
        { name: streamName, domain, offer, usePostBack, postBackUrls, landingMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCreateOpen(false);
      fetchStreams();
    } catch (e) {
      setErrorMessage(e?.response?.data?.error || "Ошибка при создании потока.");
    }
  };

  const handleArchive = async (name) => {
    if (!window.confirm(`Переместить поток "${name}" в архив?`)) return;
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${serverDomain}/api/streams/archive_stream/${name}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchStreams();
    } catch (e) {
      console.error("Ошибка при архивировании:", e);
    }
  };

  const handleRestore = async (name) => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${serverDomain}/api/streams/restore_stream/${name}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchStreams();
    } catch (e) {
      console.error("Ошибка при восстановлении:", e);
    }
  };

  const openEdit = (row) => {
    setEditingStream(row);
    setEditPostbackUrls({
      registration: row.registration_postback || "",
      subscription: row.subscription_postback || "",
      payment: row.payment_postback || "",
      start: row.start_postback || "",
    });
    setEditLandingMode(row.landing_mode || "none");
    setEditOpen(true);
  };

  const handleUpdatePostback = async () => {
    const token = localStorage.getItem("token");
    if (!token || !editingStream) return;

    try {
      await axios.put(
        `${serverDomain}/api/streams/update_postbacks/${editingStream.link_name}`,
        { postBackUrls: editPostbackUrls, landingMode: editLandingMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditOpen(false);
      fetchStreams();
    } catch (e) {
      console.error("Ошибка обновления postback:", e);
      alert("Ошибка при обновлении PostBack URL");
    }
  };

  const shownFrom = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const shownTo = Math.min(currentPage * pageSize, totalItems);

  return (
    <div>
      <TopBar pageTitle={"Потоки"} />
      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-streams">
            {/* Toolbar */}
            <div className="fl-toolbar fl-streams__toolbar">
              <button className="fl-btn" type="button" onClick={openCreate}>
                Создать поток
              </button>

              <input
                className="fl-input fl-input--wide"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Поиск по потоку / офферу / домену"
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              />

              <label className="fl-check">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                <span>Архивные</span>
              </label>

              <div className="fl-streams__meta">
                Показаны <b>{shownFrom}</b>–<b>{shownTo}</b> из <b>{totalItems}</b>
              </div>

              <div className="fl-streams__pager">
                <button
                  className="fl-btn fl-btn--ghost"
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>

                <span className="fl-streams__page">
                  {currentPage} / {totalPages}
                </span>

                <button
                  className="fl-btn fl-btn--ghost"
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="fl-table-wrap">
              <table className="fl-table fl-streams__table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th className="fl-hide-sm">Домен</th>
                    <th>Ссылка</th>
                    <th className="fl-col-icon" />
                    <th className="fl-hide-sm">Оффер</th>
                    <th className="fl-hide-sm">Дата</th>
                    <th style={{ width: 110 }} />
                  </tr>
                </thead>

                <tbody>
                  {paginatedStreams.map((row) => {
                    const rawDomain = domainNameById.get(row.domain) || "Не найдено";
                    const domainName = rawDomain === "default" ? "По умолчанию" : rawDomain;
                    const offerName = row.offer_name || "Не найдено";
                    const link = `https://t.me/fastlovetg_bot?start=${row.link_name}`;

                    return (
                      <tr key={row.link_name}>
                        <td>
                          <div className="fl-streams__name">
                            <b>{row.link_name}</b>
                            {row.deleted_at ? <span className="fl-chip">архив</span> : null}
                          </div>
                        </td>

                        <td className="fl-hide-sm">{domainName}</td>

                        <td>
                            <a
                                className="fl-linktext fl-ellipsis-start"
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                title={link}
                            >
                                <span>{link}</span>
                            </a>
                        </td>

                        <td className="fl-col-icon">
                            <button
                                className="fl-iconbtn fl-iconbtn--copy"
                                type="button"
                                title="Скопировать"
                                onClick={async () => {
                                const ok = await copyToClipboard(link);
                                setCopiedMsg(ok ? "Ссылка скопирована" : "Не удалось скопировать");
                                setTimeout(() => setCopiedMsg(""), 1200);
                                }}
                            >
                                📋
                            </button>
                        </td>

                        <td className="fl-hide-sm">{offerName}</td>
                        <td className="fl-hide-sm">{new Date(row.created_at).toLocaleString()}</td>

                        <td className="fl-col-actions">
                            {row.deleted_at ? (
                                <div className="fl-actions">
                                <button className="fl-iconbtn fl-iconbtn--restore" type="button" onClick={() => handleRestore(row.link_name)} title="Восстановить">
                                    🔄
                                </button>
                                </div>
                            ) : (
                                <div className="fl-actions">
                                <button className="fl-iconbtn fl-iconbtn--edit" type="button" onClick={() => openEdit(row)} title="Редактировать PostBack">
                                    ✏️
                                </button>
                                <button className="fl-iconbtn fl-iconbtn--trash" type="button" onClick={() => handleArchive(row.link_name)} title="В архив">
                                    🗑️
                                </button>
                                </div>
                            )}
                        </td>
                      </tr>
                    );
                  })}

                  {paginatedStreams.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: 16, opacity: 0.7 }}>
                        Ничего не найдено
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* small toast */}
            {copiedMsg ? <div className="fl-toast">{copiedMsg}</div> : null}
          </div>
        </main>
      </div>

      {/* Create modal */}
      <FlModal
        open={createOpen}
        title="Создать поток"
        onClose={() => setCreateOpen(false)}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn fl-btn--ghost" type="button" onClick={() => setCreateOpen(false)}>
              Отмена
            </button>
            <button className="fl-btn" type="button" onClick={handleSave}>
              Создать
            </button>
          </div>
        }
      >
        <div className="fl-formGrid">
          <label className="fl-labelBlock">
            Название потока
            <input
              className="fl-input fl-input--wide"
              value={streamName}
              onChange={(e) => setStreamName(validateStreamName(e.target.value))}
              placeholder="Только A-Z и 0-9"
            />
          </label>

          <div className="fl-grid2">
            <label className="fl-labelBlock">
              Домен
              <select className="fl-select" value={domain} onChange={(e) => setDomain(Number(e.target.value))}>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name === "default" ? "По умолчанию" : d.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="fl-labelBlock">
              Оффер
              <select
                className="fl-select"
                value={offer}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setOffer(next);
                  if (next !== 1) setLandingMode("none");
                }}
              >
                {offers.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="fl-labelBlock">
            Лендинг
            {Number(offer) === 1 ? (
              <select className="fl-select" value={landingMode} onChange={(e) => setLandingMode(e.target.value)}>
                <option value="none">Без лендинга</option>
                <option value="premium_1rub">Премиум за 1 RUB (тест)</option>
              </select>
            ) : (
              <select className="fl-select" value="none" disabled>
                <option value="none">Доступно только для RevShare</option>
              </select>
            )}
          </label>

          <label className="fl-check fl-check--block">
            <input type="checkbox" checked={usePostBack} onChange={(e) => setUsePostBack(e.target.checked)} />
            <span>Использовать PostBack</span>
          </label>

          {usePostBack && (
            <>
              <div className="fl-grid2">
                <label className="fl-labelBlock">
                  URL Старта
                  <input
                    className="fl-input fl-input--wide"
                    value={postBackUrls.start}
                    onChange={(e) => setPostBackUrls((p) => ({ ...p, start: e.target.value }))}
                    placeholder="http://example.com/start.php?clickid={clickid}"
                  />
                </label>

                <label className="fl-labelBlock">
                  URL Регистрация
                  <input
                    className="fl-input fl-input--wide"
                    value={postBackUrls.registration}
                    onChange={(e) => setPostBackUrls((p) => ({ ...p, registration: e.target.value }))}
                    placeholder="http://example.com/registration.php?clickid={clickid}"
                  />
                </label>
              </div>

              <div className="fl-grid2">
                <label className="fl-labelBlock">
                  URL Подписки
                  <input
                    className="fl-input fl-input--wide"
                    value={postBackUrls.subscription}
                    onChange={(e) => setPostBackUrls((p) => ({ ...p, subscription: e.target.value }))}
                    placeholder="http://example.com/trial.php?clickid={clickid}&payment={payment}"
                  />
                </label>

                <label className="fl-labelBlock">
                  URL Покупки
                  <input
                    className="fl-input fl-input--wide"
                    value={postBackUrls.payment}
                    onChange={(e) => setPostBackUrls((p) => ({ ...p, payment: e.target.value }))}
                    placeholder="http://example.com/pay.php?clickid={clickid}&payment={payment}"
                  />
                </label>
              </div>

              <button
                type="button"
                className="fl-linklike"
                onClick={() => setShowPostbackHelp((v) => !v)}
              >
                {showPostbackHelp ? "Скрыть инструкцию" : "Как настроить Postback"}
              </button>

              {showPostbackHelp && (
                <pre className="fl-helpbox">
                {
                    'Макросы:\n{clickid} — уникальный идентификатор клика\n{payment} — сумма выплаты\n\n'
                    + 'Пример start-ссылки в трекере:\nhttps://t.me/fastlovetg_bot?start=TEST-s-{clickid}\n\n'
                    + 'Telegram postback:\nhttps://api.telegram.org/bot(API TOKEN)/sendMessage?chat_id=(user_id)&text={payment}'
                }
                </pre>
              )}
            </>
          )}

          {errorMessage ? <div className="fl-error">{errorMessage}</div> : null}
        </div>
      </FlModal>

      {/* Edit modal */}
      <FlModal
        open={editOpen}
        title="Редактировать PostBack"
        onClose={() => setEditOpen(false)}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn fl-btn--ghost" type="button" onClick={() => setEditOpen(false)}>
              Отмена
            </button>
            <button className="fl-btn" type="button" onClick={handleUpdatePostback}>
              Сохранить
            </button>
          </div>
        }
      >
        {editingStream ? (
          <div className="fl-formGrid">
            <div className="fl-kv">
              <div className="fl-kv__row">
                <span>Поток</span>
                <b>{editingStream.link_name}</b>
              </div>
              <div className="fl-kv__row">
                <span>Домен</span>
                <b>{(domainNameById.get(editingStream.domain) || "—") === "default" ? "По умолчанию" : (domainNameById.get(editingStream.domain) || "—")}</b>
              </div>
              <div className="fl-kv__row">
                <span>Оффер</span>
                <b>{editingStream.offer_name || "—"}</b>
              </div>
            </div>

            <div className="fl-grid2">
              <label className="fl-labelBlock">
                URL Старта
                <input
                  className="fl-input fl-input--wide"
                  value={editPostbackUrls.start}
                  onChange={(e) => setEditPostbackUrls((p) => ({ ...p, start: e.target.value }))}
                />
              </label>

              <label className="fl-labelBlock">
                URL Регистрация
                <input
                  className="fl-input fl-input--wide"
                  value={editPostbackUrls.registration}
                  onChange={(e) => setEditPostbackUrls((p) => ({ ...p, registration: e.target.value }))}
                />
              </label>
            </div>

            <div className="fl-grid2">
              <label className="fl-labelBlock">
                URL Подписки
                <input
                  className="fl-input fl-input--wide"
                  value={editPostbackUrls.subscription}
                  onChange={(e) => setEditPostbackUrls((p) => ({ ...p, subscription: e.target.value }))}
                />
              </label>

              <label className="fl-labelBlock">
                URL Платежа
                <input
                  className="fl-input fl-input--wide"
                  value={editPostbackUrls.payment}
                  onChange={(e) => setEditPostbackUrls((p) => ({ ...p, payment: e.target.value }))}
                />
              </label>
            </div>

            <label className="fl-labelBlock">
              Лендинг
              {Number(editingStream?.offer_id || offer) === 1 ? (
                <select className="fl-select" value={editLandingMode} onChange={(e) => setEditLandingMode(e.target.value)}>
                  <option value="none">Без лендинга</option>
                  <option value="premium_1rub">Премиум за 1 RUB (тест)</option>
                </select>
              ) : (
                <select className="fl-select" value="none" disabled>
                  <option value="none">Доступно только для RevShare</option>
                </select>
              )}
            </label>
          </div>
        ) : null}
      </FlModal>
    </div>
  );
};

export default StreamsPage;